import {
  Box,
  Divider,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import strings from "global/constants/StringConstants";
import {
  debounceEventHandler,
  handleErrorMessage,
  initialPageFilterState,
  last30DaysFromDate,
  last30DaysToDate,
  validatePageQuery,
  validatePerPageQuery,
} from "helpers/methods";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import { DateRange, pageFilters } from "models/interfaces";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import SearchIcon from "assets/icons1/search_icon.svg";
import CustomTable from "global/components/CustomTable/CustomTable";
import {
  fetchRecipientDomains,
  fetchRecipientDomainsCount,
} from "./ReportsService";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { recipientDomainsTableHeader } from "./ReportsConstants";
import DashboardDateRangePicker from "screens/Dashboard/Components/DashboardDateRangePicker";

const RecipientDomains = () => {
  useTitle(title.RECIPIENT_DOMAINS);
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const searchTerm = useRef<any>(null);
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const searchFromUrl = urlParams.get("search");
  const [recipientDomains, setRecipientDomains] = useState<string[]>([]);
  const [count, setCount] = useState<number>();
  const [openDateRangePicker, setOpenDateRangePicker] =
    useState<boolean>(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    fromDate: last30DaysFromDate,
    toDate: last30DaysToDate,
  });
  const dateRangeRef = useRef(dateRange);

  const [recipientDomainsFilters, setRecipientDomainsFilters] =
    useState<pageFilters>(
      initialPageFilterState(
        pageFromUrl,
        rowsPerPageFromUrl,
        searchFromUrl ?? ""
      )
    );

  useEffect(() => {
    dateRangeRef.current = dateRange;
  }, [dateRange, setDateRange]);

  useEffect(() => {
    const { page, rowsPerPage, searchText } = recipientDomainsFilters;
    searchTerm.current.value = searchText;
    window.history.replaceState(
      null,
      "",
      `?page=${page}&rowsPerPage=${rowsPerPage}`
    );
    getTableData(dateRange);
  }, [recipientDomainsFilters]);

  const handleRowsPageData = (rowPerPage: number) => {
    setRecipientDomainsFilters({
      ...recipientDomainsFilters,
      rowsPerPage: rowPerPage,
      page: 1,
    });
  };

  const handleChangePage = async (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    setRecipientDomainsFilters({ ...recipientDomainsFilters, page });
  };

  const handleSearchOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    setRecipientDomainsFilters({
      ...recipientDomainsFilters,
      searchText,
      page: 1,
    });
  };

  const getTableData = async (dateRange: DateRange) => {
    const { page, rowsPerPage, searchText } = recipientDomainsFilters;
    try {
      setIsLoading(true);
      const count = await fetchRecipientDomainsCount(
        dateRange?.fromDate!,
        dateRange?.toDate!,
        searchText
      );
      setCount(count);
      await getTableDataAccordingToPage(
        page,
        count,
        dateRange?.fromDate!,
        dateRange?.toDate!,
        rowsPerPage,
        searchText.trim()
      );
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTableDataAccordingToPage = async (
    page: number,
    count: number,
    fromDate: Date | string | null,
    toDate: Date | string | null,
    rowsPerPage: number,
    searchText: string
  ) => {
    try {
      let recipientDomainsRes: any[] = [];
      recipientDomainsRes = await fetchRecipientDomains(
        page,
        rowsPerPage,
        fromDate,
        toDate,
        searchText
      );
      const tableData = convertDataToTableFormat(recipientDomainsRes);
      setRecipientDomains([...tableData]);
    } catch (error) {
      throw error;
    }
  };

  const getPercentage = (count: number, divideby: number) => {
    return ((count / (divideby === 0 ? 1 : divideby)) * 100).toFixed(2);
  };

  const convertDataToTableFormat = (data: any) => {
    return data.map((item: any) => {
      const renderCount = (count: number, requested: number) => {
        const percentage = getPercentage(count, requested);
        return (
          <Typography variant="body2">
            {percentage.toString() === "0.00" ? "-" : percentage}
          </Typography>
        );
      };
      return {
        domain: (
          <Typography
            sx={{ color: "#475569", fontWeight: 700 }}
            variant="subtitle2"
          >
            {item.recipientDomain}
          </Typography>
        ),
        requested_count: item.requestedCount,
        received_count: renderCount(
          item.deliveredCount ?? 0,
          item.requestedCount ?? 1
        ),
        opened_count: renderCount(
          item.openedCount ?? 0,
          item.requestedCount ?? 1
        ),

        clicked_count: renderCount(
          item.clickedCount ?? 0,
          item.requestedCount ?? 1
        ),
        bounced_count: renderCount(
          item.bouncedCount ?? 0,
          item.requestedCount ?? 1
        ),
        invalid_count: renderCount(
          item.invalidCount ?? 0,
          item.requestedCount ?? 1
        ),
        spam_count: renderCount(item.spamCount ?? 0, item.requestedCount ?? 1),
        abuse_count: renderCount(
          item.abuseCount ?? 0,
          item.requestedCount ?? 1
        ),
        dropped_count: renderCount(
          item.droppedCount ?? 0,
          item.requestedCount ?? 1
        ),
        unsubscribed_count: renderCount(
          item.unsubscribedCount ?? 0,
          item.requestedCount ?? 1
        ),
      };
    });
  };

  const getFilters = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Stack>
          <TextField
            id="contacts_search_box"
            placeholder="Search Recipient Domains"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: 0,
              },
              "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                {
                  border: 0,
                },
              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  border: 0,
                },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#fff",
                },
                "&:hover fieldset": {
                  borderColor: "#fff",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#fff",
                },
                cursor: "pointer !important",
              },
            }}
            fullWidth
            onChange={debounceEventHandler(
              handleSearchOnChange,
              strings.SEARCH_TIME_OUT
            )}
            inputRef={searchTerm}
            InputProps={{
              startAdornment: (
                <Stack mr={1}>
                  <img src={SearchIcon} width="30px" height="20px" />
                </Stack>
              ),
            }}
          />
          <Divider />
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
          >
            <DashboardDateRangePicker
              openDateRangePicker={openDateRangePicker}
              setOpenDateRangePicker={setOpenDateRangePicker}
              dateRange={dateRange}
              setDateRange={setDateRange}
              helperFunction={getTableData}
            />
          </Stack>
        </Stack>
      </CenteredCardLayout>
    );
  };

  const getHedingSection = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Stack direction="column">
          <Typography variant="h5" color="text.primary">
            Recipient Domains
          </Typography>
        </Stack>
        {getFilters()}
        <CenteredCardLayout removeLastChildBottomPadding={true}>
          <Box>
            <CustomTable
              headers={recipientDomainsTableHeader}
              rows={recipientDomains}
              page={recipientDomainsFilters.page}
              rowsPerPage={recipientDomainsFilters.rowsPerPage}
              onRowsPerPageChange={handleRowsPageData}
              onPageChange={handleChangePage}
              count={count}
              showJumpToPageNumber={false}
              handleOnChangePageInput={handleChangePage}
              showPagination={true}
              isLoading={isLoading}
            />
          </Box>
        </CenteredCardLayout>
      </Stack>
    );
  };

  return (
    <CenteredAuthenticatedLayout>
      {getHedingSection()}
    </CenteredAuthenticatedLayout>
  );
};
export default RecipientDomains;
